import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Gift, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useReferralLink } from "@/hooks/useReferralLink";
import ShareMenu from "@/components/ShareMenu";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { trackShare } from "@/lib/shareAnalytics";

interface InviteButtonProps {
  variant?: "icon" | "compact" | "full";
  className?: string;
}

/**
 * Persistent "Invite" CTA. Opens a dialog with the user's referral code,
 * a copyable link, and the ShareMenu so people can blast it everywhere.
 */
export function InviteButton({ variant = "icon", className }: InviteButtonProps) {
  const { code, link, text, rewardReferred, rewardReferrer } = useReferralLink();
  const [open, setOpen] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied");
      trackShare({ platform: "copy", surface: "invite_dialog", url: link, referralCode: code });
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const trigger =
    variant === "icon" ? (
      <Button
        variant="ghost"
        size="sm"
        className={cn(
          "rounded-full h-9 px-2.5 sm:px-3 text-xs sm:text-sm gap-1.5 hover:bg-secondary",
          className,
        )}
        aria-label="Invite friends"
      >
        <Gift className="h-3.5 w-3.5 text-primary" />
        <span className="hidden sm:inline">Invite</span>
      </Button>
    ) : variant === "compact" ? (
      <Button variant="outline" size="sm" className={cn("rounded-full", className)}>
        <Gift className="h-3.5 w-3.5 mr-1.5 text-primary" /> Invite friends
      </Button>
    ) : (
      <Button size="lg" className={cn("rounded-full shadow-glow", className)}>
        <Gift className="h-4 w-4 mr-2" /> Invite friends
      </Button>
    );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Gift className="h-5 w-5 text-primary" /> Invite friends, earn credits
          </DialogTitle>
          <DialogDescription>
            They get <span className="text-foreground font-medium">{rewardReferred} free credits</span> when they sign up.
            You get <span className="text-foreground font-medium">{rewardReferrer}</span> the moment they redeem.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-1">
          {code && (
            <div className="rounded-2xl bg-secondary p-4 text-center">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">
                Your code
              </div>
              <div className="font-display text-3xl font-semibold tracking-tight text-primary break-all">
                {code}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <input
              readOnly
              value={link}
              onFocus={(e) => e.currentTarget.select()}
              className="flex-1 min-w-0 px-3 py-2.5 rounded-full bg-secondary text-xs font-mono truncate border border-border"
            />
            <Button onClick={copyLink} variant="outline" size="icon" className="rounded-full shrink-0" aria-label="Copy link">
              <Copy className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <ShareMenu
              url={link}
              title="Glowie, your AI skincare bestie"
              text={text}
              variant="default"
              className="flex-1"
              label="Share my link"
              surface="invite_dialog"
            />
            <Button asChild variant="ghost" className="rounded-full" onClick={() => setOpen(false)}>
              <Link to="/app/referrals">
                <Sparkles className="h-4 w-4 mr-1.5" /> See referrals
              </Link>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default InviteButton;